import { memo, useEffect } from 'react';
import { useFeatureFlag, withFeatureFlagService, withScopeContext } from '@mews/framework';
import { OrderItemType, ServiceOrder } from '@mews-commander/data-access';
import { FeatureFlag } from '@mews-commander/enums';
import { Virtual } from '@optimus-web/helpers';
import { ExpandableBillListItem } from '../../../../components';
import { TotalAmountAndCount } from '../../../../types';
import { OrderItemProductGroup } from '../OrderItemProductGroup';
import { ServiceOrderItemLabel } from '../ServiceOrderItemLabel';
import { PaymentRequestList } from '../../PaymentRequestList/PaymentRequestList';
import { toggleServiceOrder, useIsServiceOrderExpanded } from '../store';
import { SelectState } from '../types';
import { connectServiceOrderItem } from './connectedServiceOrderItem';

interface ServiceOrderNestedServiceListProps {
    serviceOrders: ServiceOrder[];
    billId: string;
    month: string;
    accountId: string;
    depth?: number;
}

const ServiceOrderNestedServiceList = ({
    serviceOrders,
    billId,
    accountId,
    depth,
    month,
}: ServiceOrderNestedServiceListProps) => {
    const renderServiceOrderItem = (serviceOrder: ServiceOrder) => (
        <ServiceOrderItem
            key={serviceOrder.id}
            value={serviceOrder.id}
            serviceOrderId={serviceOrder.id}
            billId={billId}
            accountId={accountId}
            serviceId={serviceOrder.serviceId}
            depth={depth}
            month={month}
            isRebateScreen={false}
        />
    );
    const virtualProps = {
        defaultItemHeight: 40,
        overscanSize: 500,
    };

    return serviceOrders.length < 15 ? (
        <div>{serviceOrders.map(renderServiceOrderItem)}</div>
    ) : (
        <div style={{ height: 600 }}>
            <Virtual {...virtualProps}>{serviceOrders.map(renderServiceOrderItem)}</Virtual>
        </div>
    );
};

interface ServiceOrderItemProps {
    serviceId: string;
    serviceOrderId: string;
    value: string;
    billId: string;
    accountId: string;
    selectState: SelectState;
    totalAmountAndCount: TotalAmountAndCount;
    areAnyOrderItemsHighlighted: boolean;
    toggleSelect: () => void;
    orderItemTypes: OrderItemType[];
    month: string;
    depth?: number;
    reservationPayments: string[];
    reservationProductServiceOrders: ServiceOrder[];
    scope: string;
}

const ServiceOrderItemPure: React.FC<ServiceOrderItemProps> = memo(
    ({
        serviceId,
        billId,
        accountId,
        serviceOrderId,
        totalAmountAndCount,
        toggleSelect,
        orderItemTypes,
        depth = 0,
        month,
        selectState,
        areAnyOrderItemsHighlighted,
        reservationPayments,
        reservationProductServiceOrders,
        scope,
        ...other
    }) => {
        const serviceOrderKey = `${billId}-${serviceOrderId}`;

        const isExpanded = useIsServiceOrderExpanded(serviceOrderKey);

        const reservationLinkToPaymentsEnabled = useFeatureFlag(FeatureFlag.reservationLinkToPaymentsEnabled);
        const linkProductsToReservationsEnabled = useFeatureFlag(FeatureFlag.linkProductsToReservationsEnabled);

        useEffect(() => {
            if (areAnyOrderItemsHighlighted) toggleServiceOrder(serviceOrderKey, true);
        }, [areAnyOrderItemsHighlighted, serviceOrderKey]);

        const primaryText = (
            <ServiceOrderItemLabel
                totalAmountAndCount={totalAmountAndCount}
                someSelected={selectState === 'SOME' || selectState === 'ALL'}
                toggleSelect={toggleSelect}
                allSelected={selectState === 'ALL'}
                serviceOrderId={serviceOrderId}
                billId={billId}
                serviceId={serviceId}
            />
        );

        return (
            <ExpandableBillListItem
                onClick={() => toggleServiceOrder(serviceOrderKey, !isExpanded)}
                opened={isExpanded}
                primaryText={primaryText}
                multiSelect
                depth={depth}
                {...other}
            >
                {orderItemTypes.map((key) => (
                    <OrderItemProductGroup
                        type={key}
                        serviceOrderId={serviceOrderId}
                        serviceId={serviceId}
                        billId={billId}
                        key={key}
                        month={month}
                    />
                ))}
                {linkProductsToReservationsEnabled && reservationProductServiceOrders.length ? (
                    <ServiceOrderNestedServiceList
                        serviceOrders={reservationProductServiceOrders}
                        month={month}
                        billId={billId}
                        accountId={accountId}
                        depth={depth + 1}
                    />
                ) : null}

                {reservationLinkToPaymentsEnabled ? (
                    <PaymentRequestList billId={billId} accountId={accountId} serviceOrderId={serviceOrderId} />
                ) : null}
            </ExpandableBillListItem>
        );
    }
);

export const ServiceOrderItem = withScopeContext(withFeatureFlagService(connectServiceOrderItem(ServiceOrderItemPure)));
